import { memo, useCallback, useEffect, useMemo } from 'react';
import UploadProgressCard from './UploadProgressCard';
import useWorkspace from '@/hooks/useWorkspace';
import SelectDropdown from '@/components/atoms/SelectDropdown';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { CONTENT_FORMATS } from '@/Pages/Sessions/NewContentModal/constants';
import { useAppContext } from '@/context/AppContext/AppContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

/**
 * Screen 1 of the simplified upload flow: pick a workspace and upload a file.
 * File-only — no URL/link import (spec §4.1). The actual Filestack upload is
 * owned by the parent (so it survives navigation); this step only collects the
 * workspace + file and reports progress.
 */
const WorkspaceUploadStep = ({
  selectedWorkspaceId,
  onWorkspaceChange,
  onFilesSelected,
  uploadFile,
  uploadFileName,
  uploadPercentage,
  hasUploadStarted
}: {
  selectedWorkspaceId: string;
  onWorkspaceChange: (workspaceId: string) => void;
  onFilesSelected: (files: File[]) => void;
  uploadFile?: File;
  uploadFileName?: string;
  uploadPercentage: number;
  hasUploadStarted: boolean;
}) => {
  const { adminAppStore } = useAppContext();
  const { workspaces, isLoading: isLoadingWorkspaces, error: workspaceError, getWorkspaceOptions } = useWorkspace();

  const isWorkspaceEnabled = Boolean(adminAppStore?.currentOrg?.is_workspace_enabled);
  const isWorkspaceFeatureFlagEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_workspace_filters];

  // Show the dropdown as soon as we know workspaces apply — including while they load — so it
  // renders in a disabled "Loading workspaces…" state instead of popping in and shifting layout.
  const showWorkspaceDropdown = useMemo(
    () => isWorkspaceEnabled && isWorkspaceFeatureFlagEnabled,
    [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled]
  );

  // Block the file input until a workspace is chosen (also while the list is still loading, so an
  // upload can't start without one). The disabled message reflects whether we're loading or waiting
  // on a selection.
  const isWorkspaceRequired = useMemo(
    () => isWorkspaceEnabled && isWorkspaceFeatureFlagEnabled && !selectedWorkspaceId,
    [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled, selectedWorkspaceId]
  );

  const handleWorkspaceChange = useCallback(
    (workspaceName: string) => {
      const workspace = workspaces.find(w => w.name === workspaceName);
      if (workspace) {
        onWorkspaceChange(workspace.id);
      }
    },
    [workspaces, onWorkspaceChange]
  );

  // Auto-select the only / default workspace, mirroring the legacy ContentUpload behavior.
  useEffect(() => {
    if (!isWorkspaceFeatureFlagEnabled || selectedWorkspaceId || workspaces.length === 0) {
      return;
    }
    if (workspaces.length === 1) {
      onWorkspaceChange(workspaces[0].id);
      return;
    }
    if (!isWorkspaceEnabled) {
      const defaultWorkspace = workspaces.find(workspace => workspace.is_default === true);
      onWorkspaceChange((defaultWorkspace ?? workspaces[0]).id);
    }
  }, [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled, selectedWorkspaceId, workspaces, onWorkspaceChange]);

  return (
    <div className="flex flex-col gap-4">
      {showWorkspaceDropdown && (
        <div>
          <label className="mb-2 block text-sm text-slate-700">
            Workspace <span className="text-red-600">*</span>
          </label>
          <SelectDropdown
            options={getWorkspaceOptions()}
            selected={workspaces.find(w => w.id === selectedWorkspaceId)?.name || ''}
            onChange={handleWorkspaceChange}
            placeholder={isLoadingWorkspaces ? 'Loading workspaces...' : 'Select a workspace to upload files'}
            disabled={isLoadingWorkspaces || hasUploadStarted}
            className="m-0 w-full cursor-pointer rounded-md border-none text-sm outline-none hover:!border-deep-orange-600 focus:!border-deep-orange-600"
            buttonClassName="!h-10 focus:!ring-1 !font-normal"
          />
          {workspaceError && <p className="mt-1 text-xs text-red-600">{workspaceError}</p>}
        </div>
      )}

      {hasUploadStarted && uploadFileName ? (
        <UploadProgressCard file={uploadFile} fileName={uploadFileName} percentage={uploadPercentage} />
      ) : (
        <FilesInput
          onFilesUpload={onFilesSelected}
          accept={CONTENT_FORMATS}
          title="Upload a file"
          multiple={false}
          isDisabled={isWorkspaceRequired}
          disabledErrorMessage={
            isLoadingWorkspaces ? 'Loading workspaces…' : 'Please select a workspace to continue uploading your file'
          }
        />
      )}
    </div>
  );
};

export default memo(WorkspaceUploadStep);
